import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Download, Trash2, X, Search, BellRing, Pencil, MessageCircle } from "lucide-react";
import { API, STATUS_META, STATUS_ORDER, INTERESTS, SOURCE_META, emptyLead, formatError } from "./constants";

export const LeadsView = ({ user }) => {
  const isCofounder = user.role === "cofounder";
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [members, setMembers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(emptyLead);
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState("");
  const [editingLead, setEditingLead] = useState(null);
  const [editLeadForm, setEditLeadForm] = useState({ name: "", phone: "", email: "", city: "", interest: "", message: "" });
  const [savingLead, setSavingLead] = useState(false);
  const [convoLead, setConvoLead] = useState(null);
  const [convo, setConvo] = useState([]);
  const [convoLoading, setConvoLoading] = useState(false);

  const openConversation = (l) => {
    setConvoLead(l);
    setConvo([]);
    setConvoLoading(true);
    axios
      .get(`${API}/leads/${l.id}/conversation`, { withCredentials: true })
      .then((res) => setConvo(res.data))
      .catch(() => setConvo([]))
      .finally(() => setConvoLoading(false));
  };

  const fetchLeads = () => {
    axios
      .get(`${API}/leads/list`, { withCredentials: true })
      .then((res) => setLeads(res.data))
      .catch(() => setLeads([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchLeads();
    axios
      .get(`${API}/users`, { withCredentials: true })
      .then((res) => setMembers(res.data))
      .catch(() => setMembers([]));
    axios
      .get(`${API}/properties`, { withCredentials: true })
      .then((res) => setProperties(res.data))
      .catch(() => setProperties([]));
  }, []);

  const updateLead = async (id, patch) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
    try {
      const res = await axios.patch(`${API}/leads/${id}`, patch, { withCredentials: true });
      if (res.data?.pending) {
        setNotice("Change request sent — another co-founder must approve it before it applies.");
        fetchLeads();
      } else if (patch.assigned_to !== undefined || patch.property_id !== undefined) {
        fetchLeads();
      }
    } catch (err) {
      setNotice(formatError(err.response?.data?.detail));
      fetchLeads();
    }
  };

  const addLead = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await axios.post(`${API}/leads`, addForm, { withCredentials: true });
      setShowAdd(false);
      setAddForm(emptyLead);
      fetchLeads();
    } catch {
    } finally {
      setAdding(false);
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Remove this lead permanently?")) return;
    try {
      await axios.delete(`${API}/leads/${id}`, { withCredentials: true });
      setLeads((prev) => prev.filter((l) => l.id !== id));
    } catch {
    }
  };

  const startEditLead = (l) => {
    setEditLeadForm({
      name: l.name || "",
      phone: l.phone || "",
      email: l.email || "",
      city: l.city || "",
      interest: l.interest || "",
      message: l.message || "",
    });
    setEditingLead(l);
  };

  const saveLeadEdit = async (e) => {
    e.preventDefault();
    setSavingLead(true);
    try {
      const res = await axios.patch(`${API}/leads/${editingLead.id}`, editLeadForm, { withCredentials: true });
      if (res.data?.pending) {
        setNotice("Change request sent — another co-founder must approve it before it applies.");
      }
      setEditingLead(null);
      fetchLeads();
    } catch (err) {
      setNotice(formatError(err.response?.data?.detail));
    } finally {
      setSavingLead(false);
    }
  };

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const headers = ["Name", "Phone", "Email", "City", "Interest", "Source", "Property", "Message", "Status", "Assigned To", "Notes", "Follow-up", "Received"];
    const rows = leads.map((l) =>
      [
        l.name, l.phone, l.email, l.city, l.interest, SOURCE_META[l.source]?.label || "", l.property_name, l.message,
        STATUS_META[l.status || "new"].label, l.assigned_name, l.notes, l.follow_up,
        l.created_at ? new Date(l.created_at).toLocaleString("en-IN") : "",
      ].map(esc).join(",")
    );
    const csv = [headers.map(esc).join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "veera-leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const counts = leads.reduce((acc, l) => {
    const s = l.status || "new";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const q = search.trim().toLowerCase();
  const filtered = leads.filter((l) => {
    if (filter !== "all" && (l.status || "new") !== filter) return false;
    if (!q) return true;
    return [l.name, l.phone, l.email, l.city].some((v) => (v || "").toLowerCase().includes(q));
  });
  const today = new Date().toISOString().slice(0, 10);
  const due = leads.filter((l) => l.follow_up && l.follow_up <= today && !["won", "lost"].includes(l.status || "new"));

  return (
    <div data-testid="leads-view">
      {notice && (
        <div className="mt-6 flex items-center justify-between gap-4 rounded-xl border border-amber/60 bg-amber/10 px-5 py-4" data-testid="lead-notice">
          <p className="text-sm font-semibold text-navy">{notice}</p>
          <button onClick={() => setNotice("")} data-testid="notice-dismiss" className="text-gray-500 hover:text-navy" aria-label="Dismiss notice">
            <X size={16} />
          </button>
        </div>
      )}

      {due.length > 0 && (
        <div className="mt-6 rounded-xl border border-amber bg-[#FFF6E8] p-5" data-testid="reminder-banner">
          <div className="flex items-center gap-2">
            <BellRing size={16} className="text-amberdark" />
            <p className="text-sm font-bold text-navy">
              {due.length} follow-up{due.length > 1 ? "s" : ""} due today or overdue
            </p>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {due.map((l) => (
              <span key={l.id} className="rounded-full border border-amber/60 bg-white px-3 py-1 text-xs font-semibold text-navy" data-testid="reminder-lead">
                {l.name} · {l.follow_up}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="status-tabs">
        <button
          onClick={() => setFilter("all")}
          data-testid="status-tab-all"
          className={`rounded-md border px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] transition-colors duration-200 ${
            filter === "all" ? "border-navy bg-navy text-white" : "border-border bg-white text-gray-600 hover:border-navy"
          }`}
        >
          All ({leads.length})
        </button>
        {STATUS_ORDER.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`status-tab-${s}`}
            className={`rounded-md border px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] transition-colors duration-200 ${
              filter === s ? "border-navy bg-navy text-white" : "border-border bg-white text-gray-600 hover:border-navy"
            }`}
          >
            {STATUS_META[s].label} ({counts[s] || 0})
          </button>
        ))}
        <div className="relative ml-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, phone, email, city..."
            data-testid="lead-search-input"
            className="w-64 rounded-md border border-border bg-white py-2 pl-9 pr-3 text-sm text-navy outline-none transition-colors duration-200 placeholder:text-gray-400 focus:border-amber"
          />
        </div>
        <button
          onClick={() => setShowAdd(true)}
          data-testid="add-lead-btn"
          className="flex items-center gap-2 rounded-md bg-amber px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] text-navy transition-colors duration-300 hover:bg-amberdark hover:text-white"
        >
          <Plus size={14} />
          Add Lead
        </button>
        {isCofounder && (
          <button
            onClick={exportCsv}
            data-testid="export-csv-btn"
            className="flex items-center gap-2 rounded-md border-2 border-navy px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-navy transition-colors duration-300 hover:bg-navy hover:text-white"
          >
            <Download size={14} />
            Export CSV
          </button>
        )}
      </div>

      <div className="mt-6 rounded-xl border border-border bg-white p-6 shadow-soft">
        {loading ? (
          <p className="py-8 text-xs font-bold uppercase tracking-[0.2em] text-gray-500" data-testid="leads-loading">Loading leads...</p>
        ) : filtered.length === 0 ? (
          <p className="py-8 text-xs font-bold uppercase tracking-[0.2em] text-gray-500" data-testid="leads-empty">
            {q
              ? `No leads match "${search}".`
              : leads.length === 0
                ? isCofounder || user.role === "manager"
                  ? "No leads yet. They land here the moment someone submits the form — or add one manually."
                  : "Nothing assigned to you yet. Add a lead manually and it is yours automatically."
                : `No leads in "${STATUS_META[filter]?.label}" right now.`}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left" data-testid="leads-table">
              <thead>
                <tr className="border-b border-border">
                  {["Received", "Name", "Contact", "City", "Interest", "Property", "Message", "Follow-up", "Status", "Assigned To", "Notes", ...(isCofounder ? [""] : [])].map((h) => (
                    <th key={h} className="px-3 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((l, i) => {
                  const s = l.status || "new";
                  const isDue = l.follow_up && l.follow_up <= today && !["won", "lost"].includes(s);
                  const canEdit = isCofounder || !l.assigned_to || l.assigned_to === user.id;
                  return (
                    <tr key={l.id} className={`border-b border-border/60 align-top transition-colors duration-200 hover:bg-cream ${isDue ? "bg-[#FFFBF2]" : ""}`} data-testid="lead-row">
                      <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-500">
                        {l.created_at ? new Date(l.created_at).toLocaleString("en-IN") : "—"}
                      </td>
                      <td className="px-3 py-4 text-sm font-bold text-navy">
                        <span className="flex items-center gap-2">
                          {s === "new" && <span className="h-2 w-2 shrink-0 rounded-full bg-amber" title="New lead" />}
                          {l.name}
                        </span>
                        {l.source && SOURCE_META[l.source] && (
                          <span className={`mt-1.5 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] ${SOURCE_META[l.source].cls}`} data-testid={`lead-source-${i}`}>
                            {SOURCE_META[l.source].label}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-4">
                        <a href={`tel:${(l.phone || "").replace(/\s/g, "")}`} className="block whitespace-nowrap text-sm font-semibold text-amberdark hover:underline">{l.phone}</a>
                        {l.email && (
                          <a href={`mailto:${l.email}`} className="mt-1 block max-w-[180px] truncate text-xs text-gray-500 hover:text-amberdark">{l.email}</a>
                        )}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-600">{l.city || "—"}</td>
                      <td className="px-3 py-4 text-sm text-gray-600">
                        {l.interest || l.objective || "—"}
                        {(l.owns_land || l.capital_band || l.plot_size) && (
                          <span className="mt-1 block text-[11px] font-semibold text-gray-400">
                            {[l.owns_land && `Land: ${l.owns_land}`, l.plot_size, l.capital_band, l.location].filter(Boolean).join(" · ")}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-4">
                        {canEdit ? (
                          <select
                            value={l.property_id || ""}
                            onChange={(e) => updateLead(l.id, { property_id: e.target.value })}
                            data-testid={`lead-property-select-${i}`}
                            className="max-w-[150px] cursor-pointer rounded-md border border-border bg-white px-2.5 py-1.5 text-xs font-bold text-gray-700 outline-none transition-colors duration-200 focus:border-amber"
                          >
                            <option value="">No property</option>
                            {properties.map((p) => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-xs font-semibold text-gray-600">{l.property_name || "—"}</span>
                        )}
                      </td>
                      <td className="max-w-[200px] px-3 py-4 text-sm text-gray-500">
                        {l.message || "—"}
                        {l.source === "whatsapp" && (
                          <button
                            onClick={() => openConversation(l)}
                            data-testid={`lead-chat-btn-${i}`}
                            className="mt-1.5 flex items-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-green-700 transition-colors duration-200 hover:bg-green-100"
                          >
                            <MessageCircle size={11} /> View chat
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-4">
                        {canEdit ? (
                          <input
                            type="date"
                            value={l.follow_up || ""}
                            onChange={(e) => updateLead(l.id, { follow_up: e.target.value })}
                            data-testid={`lead-followup-${i}`}
                            className={`rounded-md border bg-white px-2 py-1.5 text-xs text-gray-700 outline-none transition-colors duration-200 focus:border-amber ${isDue ? "border-amber" : "border-border"}`}
                          />
                        ) : (
                          <span className="text-xs text-gray-600">{l.follow_up || "—"}</span>
                        )}
                      </td>
                      <td className="px-3 py-4">
                        {canEdit ? (
                          <select
                            value={s}
                            onChange={(e) => updateLead(l.id, { status: e.target.value })}
                            data-testid={`lead-status-select-${i}`}
                            className={`cursor-pointer rounded-md border px-2.5 py-1.5 text-xs font-bold outline-none ${STATUS_META[s].cls}`}
                          >
                            {STATUS_ORDER.map((k) => (
                              <option key={k} value={k}>{STATUS_META[k].label}</option>
                            ))}
                          </select>
                        ) : (
                          <span className={`rounded-md border px-2.5 py-1.5 text-xs font-bold ${STATUS_META[s].cls}`}>{STATUS_META[s].label}</span>
                        )}
                      </td>
                      <td className="px-3 py-4">
                        {canEdit ? (
                          <select
                            value={l.assigned_to || ""}
                            onChange={(e) => updateLead(l.id, { assigned_to: e.target.value })}
                            data-testid={`assign-select-${i}`}
                            className="cursor-pointer rounded-md border border-border bg-white px-2.5 py-1.5 text-xs font-bold text-gray-700 outline-none transition-colors duration-200 focus:border-amber"
                          >
                            <option value="">Unassigned</option>
                            {members.map((m) => (
                              <option key={m.id} value={m.id}>{m.name}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-xs font-semibold text-gray-600">{l.assigned_name || "—"}</span>
                        )}
                      </td>
                      <td className="px-3 py-4">
                        {canEdit ? (
                          <input
                            type="text"
                            defaultValue={l.notes || ""}
                            onBlur={(e) => {
                              if (e.target.value !== (l.notes || "")) updateLead(l.id, { notes: e.target.value });
                            }}
                            placeholder="Add note..."
                            data-testid={`lead-notes-input-${i}`}
                            className="w-40 rounded-md border border-border bg-white px-2.5 py-1.5 text-xs text-gray-700 outline-none transition-colors duration-200 placeholder:text-gray-400 focus:border-amber"
                          />
                        ) : (
                          <span className="text-xs text-gray-500">{l.notes || "—"}</span>
                        )}
                        {l.last_modified_by_name && (
                          <p className="mt-1 text-[10px] text-gray-400">Last edit: {l.last_modified_by_name}</p>
                        )}
                      </td>
                      {isCofounder && (
                        <td className="px-3 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => startEditLead(l)}
                              data-testid={`lead-edit-btn-${i}`}
                              aria-label={`Edit lead ${l.name}`}
                              className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-gray-400 transition-colors duration-200 hover:border-amber hover:bg-amber/10 hover:text-amberdark"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              onClick={() => deleteLead(l.id)}
                              data-testid={`lead-delete-btn-${i}`}
                              aria-label={`Delete lead ${l.name}`}
                              className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-gray-400 transition-colors duration-200 hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {convoLead && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-navy/60 p-4 backdrop-blur-sm"
          data-testid="lead-convo-modal"
          onClick={() => setConvoLead(null)}
        >
          <div
            className="relative flex max-h-[80vh] w-full max-w-md flex-col rounded-xl bg-white p-6 shadow-lift"
            data-lenis-prevent="true"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={() => setConvoLead(null)} data-testid="convo-close-btn" aria-label="Close conversation" className="absolute right-4 top-4 text-gray-400 transition-colors duration-200 hover:text-navy">
              <X size={18} />
            </button>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">WhatsApp conversation</p>
            <h3 className="mt-1 text-lg font-extrabold text-navy">{convoLead.name}</h3>
            <p className="text-xs text-gray-500">{convoLead.phone}</p>
            <div className="mt-4 flex-1 space-y-3 overflow-y-auto pr-1" data-testid="convo-thread">
              {convoLoading ? (
                <p className="py-6 text-center text-xs font-bold uppercase tracking-[0.2em] text-gray-400">Loading...</p>
              ) : convo.length === 0 ? (
                <p className="py-6 text-center text-xs font-bold uppercase tracking-[0.2em] text-gray-400" data-testid="convo-empty">
                  No WhatsApp messages linked to this lead yet.
                </p>
              ) : (
                convo.map((m, i) => (
                  <div
                    key={i}
                    data-testid={`convo-msg-${i}`}
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.direction === "out" ? "ml-auto bg-navy text-white" : "bg-cream text-navy"}`}
                  >
                    <p className="whitespace-pre-wrap">{m.text}</p>
                    <p className={`mt-1 text-[10px] ${m.direction === "out" ? "text-white/60" : "text-gray-400"}`}>
                      {m.ts ? new Date(m.ts).toLocaleString("en-IN") : ""}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {showAdd && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-navy/60 p-4 backdrop-blur-sm"
          data-testid="add-lead-modal"
          onClick={() => setShowAdd(false)}
        >
          <div
            className="relative max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-8 shadow-lift"
            data-lenis-prevent="true"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowAdd(false)}
              data-testid="add-lead-cancel"
              aria-label="Close add lead form"
              className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-md border border-border text-gray-500 transition-colors duration-300 hover:border-navy hover:text-navy"
            >
              <X size={16} />
            </button>
            <h3 className="font-display text-2xl font-extrabold tracking-tight text-navy" data-testid="add-lead-heading">
              Add a lead manually
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-gray-600">
              For people who call or walk in directly. The lead is assigned to
              you automatically — reassign to a colleague anytime.
            </p>
            <form onSubmit={addLead} className="mt-7 space-y-6" data-testid="add-lead-form">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="add-name" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Name</label>
                  <input id="add-name" data-testid="add-name-input" required value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} placeholder="Full name" className="field-input mt-2" />
                </div>
                <div>
                  <label htmlFor="add-phone" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Phone</label>
                  <input id="add-phone" data-testid="add-phone-input" required type="tel" value={addForm.phone} onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })} placeholder="+91 ..." className="field-input mt-2" />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="add-email" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Email <span className="normal-case tracking-normal text-gray-400">(optional)</span></label>
                  <input id="add-email" data-testid="add-email-input" type="email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} placeholder="you@example.com" className="field-input mt-2" />
                </div>
                <div>
                  <label htmlFor="add-city" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">City</label>
                  <input id="add-city" data-testid="add-city-input" value={addForm.city} onChange={(e) => setAddForm({ ...addForm, city: e.target.value })} placeholder="Bangalore..." className="field-input mt-2" />
                </div>
              </div>
              <div>
                <label htmlFor="add-interest" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Interested in</label>
                <select id="add-interest" data-testid="add-interest-select" value={addForm.interest} onChange={(e) => setAddForm({ ...addForm, interest: e.target.value })} className="field-input mt-2">
                  {INTERESTS.map((i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="add-message" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Notes from the call (optional)</label>
                <textarea id="add-message" data-testid="add-message-input" rows={2} value={addForm.message} onChange={(e) => setAddForm({ ...addForm, message: e.target.value })} placeholder="What did they ask about?" className="field-input mt-2 resize-none" />
              </div>
              <button
                type="submit"
                data-testid="add-lead-submit"
                disabled={adding}
                className="w-full rounded-md bg-amber px-8 py-4 text-sm font-bold text-navy transition-colors duration-300 hover:bg-amberdark hover:text-white disabled:opacity-50"
              >
                {adding ? "Adding..." : "Add to Pipeline"}
              </button>
            </form>
          </div>
        </div>
      )}

      {editingLead && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-navy/60 p-4 backdrop-blur-sm"
          data-testid="edit-lead-modal"
          onClick={() => setEditingLead(null)}
        >
          <div
            className="relative max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-8 shadow-lift"
            data-lenis-prevent="true"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setEditingLead(null)}
              data-testid="edit-lead-cancel"
              aria-label="Close edit lead form"
              className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-md border border-border text-gray-500 transition-colors duration-300 hover:border-navy hover:text-navy"
            >
              <X size={16} />
            </button>
            <h3 className="font-display text-2xl font-extrabold tracking-tight text-navy" data-testid="edit-lead-heading">
              Edit customer details
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-gray-600">
              Co-founder only — fix names, numbers and requirements captured wrong.
            </p>
            <form onSubmit={saveLeadEdit} className="mt-7 space-y-6" data-testid="edit-lead-form">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="edit-lead-name" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Name</label>
                  <input id="edit-lead-name" data-testid="edit-lead-name-input" required value={editLeadForm.name} onChange={(e) => setEditLeadForm({ ...editLeadForm, name: e.target.value })} className="field-input mt-2" />
                </div>
                <div>
                  <label htmlFor="edit-lead-phone" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Phone</label>
                  <input id="edit-lead-phone" data-testid="edit-lead-phone-input" required type="tel" value={editLeadForm.phone} onChange={(e) => setEditLeadForm({ ...editLeadForm, phone: e.target.value })} className="field-input mt-2" />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="edit-lead-email" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Email</label>
                  <input id="edit-lead-email" data-testid="edit-lead-email-input" type="email" value={editLeadForm.email} onChange={(e) => setEditLeadForm({ ...editLeadForm, email: e.target.value })} className="field-input mt-2" />
                </div>
                <div>
                  <label htmlFor="edit-lead-city" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">City</label>
                  <input id="edit-lead-city" data-testid="edit-lead-city-input" value={editLeadForm.city} onChange={(e) => setEditLeadForm({ ...editLeadForm, city: e.target.value })} className="field-input mt-2" />
                </div>
              </div>
              <div>
                <label htmlFor="edit-lead-interest" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Interested in</label>
                <select id="edit-lead-interest" data-testid="edit-lead-interest-select" value={editLeadForm.interest} onChange={(e) => setEditLeadForm({ ...editLeadForm, interest: e.target.value })} className="field-input mt-2">
                  {[...new Set([...INTERESTS, editLeadForm.interest].filter(Boolean))].map((i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="edit-lead-message" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Requirement / message</label>
                <textarea id="edit-lead-message" data-testid="edit-lead-message-input" rows={3} value={editLeadForm.message} onChange={(e) => setEditLeadForm({ ...editLeadForm, message: e.target.value })} className="field-input mt-2 resize-none" />
              </div>
              <button
                type="submit"
                data-testid="edit-lead-submit"
                disabled={savingLead}
                className="w-full rounded-md bg-amber px-8 py-4 text-sm font-bold text-white transition-colors duration-300 hover:bg-amberdark disabled:opacity-50"
              >
                {savingLead ? "Saving..." : "Save Changes"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
